/**
 * Component that generates and applies global font-face styles.
 *
 * This component uses the `@emotion/react` library to inject global styles for custom fonts.
 * It dynamically generates the `@font-face` CSS rules based on the `fontImports` configuration.
 *
 * @returns {JSX.Element} A React component that applies global font-face styles.
 */
import React from 'react'
import {Global} from '@emotion/react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
    delonghiFontImports,
    nutribulletFontImports,
    braunFontImports,
} from '../../theme/import/utils'
import {BRAND_KEY} from '../../brand'
import Helmet from 'react-helmet'

export const Fonts = () => {
    let fonts
    switch (BRAND_KEY) {
        case 'nutribullet':
            fonts = nutribulletFontImports
            break
        case 'delonghi':
            fonts = delonghiFontImports
            break
        case 'braun':
            fonts = braunFontImports
            break
        default:
            fonts = []
            break
    }

    const fontFaceGenerate = () => {
        var importFontString = ''

        fonts.forEach((font) => {
            font.variants.forEach((variant) => {
                const fileName = `${font.fileName ?? font.family}${variant.name}`
                // If fileName is defined, use it to build the path (useful when the file name differs from the font family name)
                const fontPath = `static/fonts/${font.folder}/${
                    font.fileName ?? font.family
                }/${fileName}`
                const options = font.options ?? {}

                importFontString += `
                @font-face {
                    font-family: '${font.family}';
                    src: url(${getAssetUrl(`${fontPath}.woff2`)}) format('woff2');
                    font-display: swap;
                    font-weight: ${variant.weight};
                    ${options.style ? `font-style: ${options.style};` : ''}
                    ${options.unicodeRange ? `unicode-range: ${options.unicodeRange};` : ''}
                }
                `
            })
        })

        return importFontString
    }

    if (BRAND_KEY === 'kenwood') {
        return (
            <Helmet>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap"
                    rel="stylesheet"
                />
            </Helmet>
        )
    }

    // Collect all fonts that have preload: true in their options
    const preloadFonts = fonts.flatMap((font) => {
        if (!font.options?.preload) return []
        return font.variants.map((variant) => {
            const fileName = `${font.fileName ?? font.family}${variant.name}`
            // If fileName is defined, use it to build the path (useful when the file name differs from the font family name)
            const fontPath = `static/fonts/${font.folder}/${
                font.fileName ?? font.family
            }/${fileName}`
            return `${fontPath}.woff2`
        })
    })

    const generatedStyleForFonts = fontFaceGenerate()

    return (
        <>
            {preloadFonts.length > 0 && (
                <Helmet>
                    {preloadFonts.map((path) => (
                        <link
                            key={path}
                            rel="preload"
                            as="font"
                            type="font/woff2"
                            crossOrigin="anonymous"
                            href={getAssetUrl(path)}
                        />
                    ))}
                </Helmet>
            )}
            <Global styles={generatedStyleForFonts} />
        </>
    )
}
